import {forwardRef, useEffect, useRef} from 'react';

import styled from '@emotion/styled';
import ReactTextareaAutosize from 'react-textarea-autosize';

import theme from 'theme';

import {InputCaption, Label, useInput} from 'Input';

import type {MouseEventHandler} from 'react';
import type {CSSObject} from '@emotion/styled';
import type {TextareaAutosizeProps} from 'react-textarea-autosize';
import type {InputInternalProps, InputProps, InputStyles} from 'Input';

import type {TextareaProps} from './types';

const textAreaStyle = (props: TextareaProps, {style, inputProps}: InputInternalProps) => {
  const styles: CSSObject = {
    ...theme.typography.p1,

    resize: props.resize,
    width: props.width,
    maxWidth: props.maxWidth,

    cursor: style.isReadOnly ? 'default' : undefined,

    borderWidth: 1,
    borderColor: theme.colors.grey[30],
    color: theme.colors.grey[style.isDisabled ? 40 : 100],

    ...inputProps?.style,
  };

  if (style.isError) {
    styles.borderWidth = 2;
    styles.borderColor = theme.colors.additional.red[80];
  } else if (style.isDisabled) {
    styles.borderWidth = 1;
    styles.borderColor = theme.colors.grey[20];
  } else if (style.isFocused) {
    styles.borderWidth = 2;
    styles.borderColor = theme.colors.accent[90];
  } else if (style.isUnborder) {
    styles.borderWidth = 0;
  }

  if (styles.borderWidth === 2) {
    styles.padding = style.isFilled ? '18px 10px 0' : '10px 10px';
  } else {
    styles.padding = style.isFilled ? '19px 11px 0' : '11px 12px';
  }

  return styles;
};

const Container = styled.div<Pick<InputStyles, 'isDisabled' | 'isFocused'>>`
  position: relative;

  * {
    ${theme.typography.base}
  }

  :hover textarea {
    ${({isDisabled, isFocused}) => {
      if (isDisabled || isFocused) {
        return;
      }

      return {
        borderWidth: 2,
        borderColor: theme.colors.grey[40],
      };
    }}
  }

  textarea {
    margin: 0;
    min-height: 48px;
    outline: none;
    border: none;
    background-color: transparent;
    vertical-align: top;
    box-sizing: border-box;
    border-style: solid;
    border-radius: 4px;
    caret-color: ${theme.colors.accent[90]};

    ${theme.scroll};
  }
`;

const LabelContainer = styled.div`
  position: absolute;
  pointer-events: none;
  left: 12px;
  top: 2px;
  height: 18px;
  background-color: ${theme.colors.white};
`;

export const Textarea = forwardRef<HTMLDivElement, TextareaProps>((props, ref) => {
  const {onHeightChange, cacheMeasurements, maxRows, minRows = 6, ...rest} = props;

  const inputProps = useInput(rest as InputProps);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  const setLabelWidth = (width?: number) => {
    if (!width || !labelRef.current) {
      return;
    }

    labelRef.current.style.width = `${width - 30}px`;
  };

  const onMouseMove: MouseEventHandler = e => {
    setLabelWidth((e.target as HTMLTextAreaElement)?.clientWidth);
  };

  useEffect(() => {
    setLabelWidth(inputRef.current?.clientWidth);
  }, [props.width, props.maxWidth]);

  const containerProps = {
    ...inputProps.style,

    className: props.className,
    isDisabled: inputProps.style.isDisabled,
    isFocused: inputProps.style.isFocused,
  };

  const textareaProps = {
    ...inputProps.inputProps,

    onMouseMove,

    onHeightChange,
    cacheMeasurements,
    maxRows,
    minRows,

    value: props.value,
    onInput: props.onChange,

    style: textAreaStyle(props, inputProps),

    'data-qa': props.qa ?? 'textarea',
  } as TextareaAutosizeProps;

  return (
    <Container ref={ref} {...containerProps}>
      <ReactTextareaAutosize ref={inputRef} {...textareaProps} />

      <LabelContainer ref={labelRef}>
        <Label {...inputProps} />
      </LabelContainer>

      <InputCaption {...inputProps} />
    </Container>
  );
});
